import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  alpha,
  Button,
  TextField,
  Divider,
} from '@mui/material';
import {
  AssessmentRounded,
  DownloadRounded,
  RefreshRounded,
  InsightsRounded,
  PictureAsPdfRounded,
  TableChartRounded,
  TimelineRounded,
  LayersRounded,
} from '@mui/icons-material';
import api, { errorMessage } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { palette } from '../theme';
import { GlassPanel, SectionTitle, EmptyState, PanelLoader, StatusPill } from '../components/ui/GlassPanel';
import { StatCard3D } from '../components/ui/StatCard3D';
import { HoloLineChart } from '../components/charts/HoloChart';
import DataTable from '../components/data/DataTable';
import { useToast } from '../components/ui/ToastProvider';

const isoDay = (date) => date.toISOString().slice(0, 10);

export default function Reports() {
  const toast = useToast();
  const [range, setRange] = useState({
    start: isoDay(new Date(Date.now() - 7 * 86400000)),
    end: isoDay(new Date()),
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/reports', { params: range });
      setReport(response.data);
    } catch (error) {
      toast.error(errorMessage(error, 'The report could not be generated.'));
    } finally {
      setLoading(false);
    }
  }, [range, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const exportReport = async () => {
    if (!report) return;
    setExporting(true);
    try {
      const jsPDF = (await import('jspdf')).default;
      const autoTable = (await import('jspdf-autotable')).default;
      const doc = new jsPDF();

      doc.setFontSize(20);
      doc.setTextColor(5, 8, 22);
      doc.text('BLUE NODE — Operations Report', 14, 20);
      doc.setFontSize(10);
      doc.setTextColor(90);
      doc.text(`Window: ${range.start} to ${range.end}`, 14, 28);
      doc.text(
        `Devices ${report.totals.devices} · Online ${report.totals.online} · Data points ${report.totals.data_points} · Alerts ${report.totals.alerts} · Uptime ${report.totals.uptime_pct}%`,
        14,
        35
      );

      autoTable(doc, {
        head: [['Device ID', 'Type', 'Status', 'Location', 'Firmware', 'Data points', 'Alerts', 'Critical']],
        body: report.devices.map((device) => [
          device.device_id,
          device.device_type,
          device.status,
          device.location || '—',
          device.firmware_version,
          device.data_points,
          device.alerts,
          device.critical_alerts,
        ]),
        startY: 44,
        styles: { fontSize: 9, cellPadding: 2.5 },
        headStyles: { fillColor: [11, 18, 36], textColor: [248, 250, 252], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [240, 244, 250] },
      });

      doc.save(`bluenode_report_${isoDay(new Date())}.pdf`);
      toast.success('Report exported as PDF.');
    } catch (error) {
      toast.error(errorMessage(error, 'The PDF export failed.'));
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    { key: 'device_id', label: 'Device ID', width: 170 },
    { key: 'device_type', label: 'Type' },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <StatusPill
          label={row.status}
          tone={row.status === 'online' ? 'success' : row.status === 'updating' ? 'warning' : 'danger'}
          pulse={false}
        />
      ),
    },
    { key: 'location', label: 'Location', render: (row) => row.location || '—' },
    { key: 'firmware_version', label: 'Firmware' },
    {
      key: 'data_points',
      label: 'Data points',
      align: 'right',
      render: (row) => <Typography className="mono">{row.data_points}</Typography>,
    },
    {
      key: 'alerts',
      label: 'Alerts',
      align: 'right',
      render: (row) => (
        <Typography className="mono" sx={{ color: row.alerts ? palette.warning : 'text.secondary' }}>
          {row.alerts}
        </Typography>
      ),
    },
    {
      key: 'critical_alerts',
      label: 'Critical',
      align: 'right',
      render: (row) => (
        <Typography className="mono" sx={{ color: row.critical_alerts ? palette.danger : 'text.secondary' }}>
          {row.critical_alerts}
        </Typography>
      ),
    },
  ];

  const totals = report?.totals || {};
  const severity = report?.severity_counts || {};
  const timeline = report?.timeline || [];
  const parameters = report?.parameters || [];
  const deviceTypes = report?.device_types || [];
  const reliability = report?.reliability || {};
  const topDevices = report?.top_devices || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <GlassPanel sx={{ p: 3 }}>
        <SectionTitle
          title="Operational Report"
          subtitle="Fleet performance across the selected reporting window"
          icon={<AssessmentRounded sx={{ fontSize: 18 }} />}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                type="date"
                size="small"
                label="From"
                value={range.start}
                onChange={(event) => setRange({ ...range, start: event.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                size="small"
                label="To"
                value={range.end}
                onChange={(event) => setRange({ ...range, end: event.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <Button size="small" variant="outlined" startIcon={<RefreshRounded sx={{ fontSize: 15 }} />} onClick={load}>
                Run
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<PictureAsPdfRounded sx={{ fontSize: 15 }} />}
                onClick={exportReport}
                disabled={!report || exporting}
              >
                PDF
              </Button>
            </Box>
          }
        />
      </GlassPanel>

      {loading ? (
        <GlassPanel>
          <PanelLoader label="Aggregating fleet analytics" />
        </GlassPanel>
      ) : !report ? (
        <GlassPanel>
          <EmptyState icon={<AssessmentRounded />} title="No report data" description="Adjust the reporting window and try again." minHeight={240} />
        </GlassPanel>
      ) : (
        <>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard3D title="Devices" value={totals.devices || 0} icon={<TableChartRounded />} accent={palette.primary} caption={`${totals.online || 0} online · ${totals.offline || 0} offline`} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard3D title="Uptime" value={totals.uptime_pct || 0} suffix="%" icon={<InsightsRounded />} accent={palette.success} caption="Fleet availability in the reporting window" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard3D title="Data Points" value={totals.data_points || 0} icon={<DownloadRounded />} accent={palette.secondary} caption={`${totals.messages_per_hour || 0} messages / hour`} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard3D title="Alerts" value={totals.alerts || 0} icon={<AssessmentRounded />} accent={palette.danger} caption={`${severity.critical || 0} critical · ${severity.warning || 0} warning`} />
            </Grid>
          </Grid>

          <GlassPanel sx={{ p: 3 }}>
            <SectionTitle title="Severity Distribution" subtitle="Alert volume by severity for the window" icon={<InsightsRounded sx={{ fontSize: 18 }} />} />
            <Box sx={{ mt: 2.25, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {[
                { label: 'Critical', value: severity.critical || 0, color: palette.danger },
                { label: 'Warning', value: severity.warning || 0, color: palette.warning },
                { label: 'Info', value: severity.info || 0, color: palette.primary },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    flex: '1 1 160px',
                    p: 2,
                    borderRadius: 3,
                    border: `1px solid ${alpha(item.color, 0.22)}`,
                    background: alpha(item.color, 0.07),
                  }}
                >
                  <Typography className="mono" sx={{ fontSize: '1.35rem', fontWeight: 500, color: item.color }}>
                    {item.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.66rem', letterSpacing: '0.14em', color: 'text.secondary' }}>
                    {item.label.toUpperCase()}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Divider sx={{ borderColor: alpha(palette.primary, 0.1), my: 2.5 }} />
            <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
              Window {formatDateTime(report.window.start)} → {formatDateTime(report.window.end)} ({report.window.days} days)
            </Typography>
          </GlassPanel>


          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={7}>
              <GlassPanel sx={{ p: 3, height: '100%' }}>
                <SectionTitle
                  title="Activity Timeline"
                  subtitle="Data points and alerts per day across the window"
                  icon={<TimelineRounded sx={{ fontSize: 18 }} />}
                />
                <Box sx={{ mt: 2.5 }}>
                  {timeline.length ? (
                    <HoloLineChart
                      labels={timeline.map((point) => point.label)}
                      datasets={[
                        { label: 'Data points', data: timeline.map((point) => point.data_points), color: palette.primary },
                        { label: 'Alerts', data: timeline.map((point) => point.alerts), color: palette.danger },
                      ]}
                      height={260}
                      legend
                    />
                  ) : (
                    <EmptyState title="No activity" description="No telemetry was recorded in this window." minHeight={220} />
                  )}
                </Box>
              </GlassPanel>
            </Grid>

            <Grid item xs={12} lg={5}>
              <GlassPanel sx={{ p: 3, height: '100%' }}>
                <SectionTitle
                  title="Reliability"
                  subtitle="Reporting coverage and alert pressure"
                  icon={<InsightsRounded sx={{ fontSize: 18 }} />}
                />
                <Box sx={{ mt: 2, display: 'grid', gap: 1.25, gridTemplateColumns: '1fr 1fr' }}>
                  {[
                    { label: 'Reporting devices', value: reliability.reporting_devices || 0, tone: palette.success },
                    { label: 'Silent devices', value: reliability.silent_devices || 0, tone: palette.warning },
                    { label: 'Alerts / 1k points', value: reliability.alert_rate_per_1k || 0, tone: palette.danger },
                    { label: 'Alerts per device', value: reliability.alerts_per_device || 0, tone: palette.warning },
                    { label: 'Points per device', value: reliability.points_per_device || 0, tone: palette.primary },
                    { label: 'Messages / hour', value: totals.messages_per_hour || 0, tone: palette.secondary },
                  ].map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        border: `1px solid ${alpha(item.tone, 0.18)}`,
                        background: alpha(item.tone, 0.05),
                      }}
                    >
                      <Typography sx={{ fontSize: '0.62rem', letterSpacing: '0.12em', color: 'text.secondary' }}>
                        {item.label.toUpperCase()}
                      </Typography>
                      <Typography className="mono" sx={{ fontSize: '1.15rem', fontWeight: 500, color: item.tone, mt: 0.4 }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </GlassPanel>
            </Grid>
          </Grid>

          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={7}>
              <GlassPanel sx={{ p: 3, height: '100%' }}>
                <SectionTitle
                  title="Parameter Statistics"
                  subtitle="Distribution of every numeric telemetry channel"
                  icon={<TableChartRounded sx={{ fontSize: 18 }} />}
                />
                <Box sx={{ mt: 2, overflowX: 'auto' }}>
                  {parameters.length === 0 ? (
                    <EmptyState title="No numeric channels" description="No measurements were recorded in this window." minHeight={200} />
                  ) : (
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                      <Box component="thead">
                        <Box component="tr">
                          {['Parameter', 'Samples', 'Min', 'Median', 'Avg', 'Max'].map((header) => (
                            <Box
                              component="th"
                              key={header}
                              sx={{
                                textAlign: header === 'Parameter' ? 'left' : 'right',
                                py: 1.1,
                                px: 1.25,
                                fontSize: '0.64rem',
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                color: 'text.secondary',
                                borderBottom: `1px solid ${alpha(palette.primary, 0.12)}`,
                                fontWeight: 700,
                              }}
                            >
                              {header}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                      <Box component="tbody">
                        {parameters.map((item) => (
                          <Box component="tr" key={item.name} sx={{ '&:hover': { background: alpha(palette.primary, 0.04) } }}>
                            <Box component="td" sx={{ py: 1.2, px: 1.25 }}>
                              <Typography className="mono" sx={{ fontSize: '0.76rem' }}>
                                {item.name}
                              </Typography>
                            </Box>
                            <Box component="td" sx={{ py: 1.2, px: 1.25, textAlign: 'right' }}>
                              <Typography className="mono" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                {item.samples}
                              </Typography>
                            </Box>
                            <Box component="td" sx={{ py: 1.2, px: 1.25, textAlign: 'right' }}>
                              <Typography className="mono" sx={{ fontSize: '0.75rem' }}>
                                {item.min}
                              </Typography>
                            </Box>
                            <Box component="td" sx={{ py: 1.2, px: 1.25, textAlign: 'right' }}>
                              <Typography className="mono" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                {item.median}
                              </Typography>
                            </Box>
                            <Box component="td" sx={{ py: 1.2, px: 1.25, textAlign: 'right' }}>
                              <Typography className="mono" sx={{ fontSize: '0.75rem', fontWeight: 600, color: palette.primary }}>
                                {item.avg}
                              </Typography>
                            </Box>
                            <Box component="td" sx={{ py: 1.2, px: 1.25, textAlign: 'right' }}>
                              <Typography className="mono" sx={{ fontSize: '0.75rem' }}>
                                {item.max}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </GlassPanel>
            </Grid>

            <Grid item xs={12} lg={5}>
              <GlassPanel sx={{ p: 3, height: '100%' }}>
                <SectionTitle
                  title="Fleet Composition"
                  subtitle="Endpoints by model with their telemetry volume"
                  icon={<LayersRounded sx={{ fontSize: 18 }} />}
                />
                <Box sx={{ mt: 2, display: 'grid', gap: 1.25 }}>
                  {deviceTypes.length === 0 && (
                    <EmptyState title="No endpoints" description="No devices exist for this scope." minHeight={160} />
                  )}
                  {deviceTypes.map((item) => {
                    const share = totals.data_points ? (item.data_points / totals.data_points) * 100 : 0;
                    return (
                      <Box key={item.type}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, flex: 1 }}>{item.type}</Typography>
                          <Typography className="mono" sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                            {item.devices} node{item.devices === 1 ? '' : 's'}
                          </Typography>
                          <Typography className="mono" sx={{ fontSize: '0.72rem', color: palette.primary }}>
                            {item.data_points} pts
                          </Typography>
                        </Box>
                        <Box sx={{ height: 5, borderRadius: 4, background: alpha(palette.primary, 0.12), overflow: 'hidden' }}>
                          <Box
                            sx={{
                              height: '100%',
                              width: `${Math.min(100, share)}%`,
                              borderRadius: 4,
                              background: `linear-gradient(90deg, ${alpha(palette.primary, 0.65)}, ${palette.primary})`,
                              boxShadow: `0 0 12px ${alpha(palette.primary, 0.55)}`,
                              transition: 'width 700ms cubic-bezier(0.22,0.9,0.28,1)',
                            }}
                          />
                        </Box>
                        {item.alerts > 0 && (
                          <Typography sx={{ fontSize: '0.66rem', color: palette.warning, mt: 0.4 }}>
                            {item.alerts} alert{item.alerts === 1 ? '' : 's'} raised
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>

                {topDevices.length > 0 && (
                  <>
                    <Divider sx={{ borderColor: alpha(palette.primary, 0.1), my: 2.25 }} />
                    <Typography sx={{ fontSize: '0.64rem', letterSpacing: '0.14em', color: 'text.secondary', mb: 1 }}>
                      HIGHEST VOLUME ENDPOINTS
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 0.75 }}>
                      {topDevices.map((device, index) => (
                        <Box key={device.device_id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography className="mono" sx={{ fontSize: '0.68rem', color: 'text.secondary', width: 16 }}>
                            {index + 1}
                          </Typography>
                          <Typography className="mono" sx={{ fontSize: '0.74rem', flex: 1, noWrap: 'ellipsis', overflow: 'hidden' }}>
                            {device.device_id}
                          </Typography>
                          <Typography className="mono" sx={{ fontSize: '0.72rem', color: palette.primary }}>
                            {device.data_points}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </GlassPanel>
            </Grid>
          </Grid>

          <GlassPanel>
            <DataTable
              columns={columns}
              rows={report.devices}
              rowKey={(row) => row.device_id}
              searchKeys={['device_id', 'device_type', 'location']}
              searchPlaceholder="Search report rows…"
              filters={[
                {
                  key: 'status',
                  label: 'Status',
                  options: [
                    { value: 'online', label: 'Online' },
                    { value: 'offline', label: 'Offline' },
                    { value: 'updating', label: 'Updating' },
                  ],
                },
              ]}
              emptyIcon={<TableChartRounded />}
              emptyTitle="No device activity"
              emptyDescription="No telemetry was recorded inside the selected window."
              pageSize={10}
            />
          </GlassPanel>
        </>
      )}
    </Box>
  );
}
